import { useQueryParameter } from '@/context/QueryParameter';
import {
  ReceivedInvoicesSearchQuery,
  TReceivedInvoiceCondition,
} from '@/context/services/invoice/type';
import { convertToInvoiceFilterPayload } from '@/features/invoices/components/List/Search/helper';
import { saveLastListSearch } from '@/features/invoices/utils/listSearchStorage';
import { useRenewalUiReceivedInvoicesFlag } from '@/hooks/feature_flag/useRenewalUiReceivedInvoicesFlag';
import {
  convertToURLSearchParams,
  useUpdateURLSearchParams,
} from '@/hooks/useConvertToURLSearchParams';
import { useCallback, useEffect, useMemo, useState } from 'react';
type TReceivedInvoiceListProps = () => {
  selectedId: string;
  onDrawerOpen: (id: string) => void;
  onDrawerClose: () => void;
};

export const useReceivedInvoiceList: TReceivedInvoiceListProps = () => {
  const [param, setParam] = useQueryParameter();
  const selectedIdWithPrefix = param.get('id') || '';
  const selectedId =
    selectedIdWithPrefix.replace(/^IV-(\d+)$/, '$1') || selectedIdWithPrefix;

  const onDrawerOpen = useCallback(
    (id: string) => {
      if (id) {
        param.set('id', id);
        setParam(param);
      }
    },
    [param, setParam]
  );

  const onDrawerClose = useCallback(() => {
    param.delete('id');
    setParam(param);
  }, [param, setParam]);

  return {
    selectedId,
    onDrawerOpen,
    onDrawerClose,
  };
};

export const useReceivedInvoiceSearchQuery = (
  defaultValues: TReceivedInvoiceCondition
) => {
  const [param] = useQueryParameter();
  const initialSearchQuery = useMemo(
    () => convertToInvoiceFilterPayload(defaultValues),
    [defaultValues]
  );

  const [searchQuery, setSearchQuery] =
    useState<ReceivedInvoicesSearchQuery>(initialSearchQuery);
  const updateSearchParams = useUpdateURLSearchParams();
  const keepSearchConditions = useRenewalUiReceivedInvoicesFlag();

  useEffect(() => {
    setSearchQuery(initialSearchQuery);
  }, [initialSearchQuery]);

  const persistLastSearch = useCallback(
    (searchQueryForUrl: Record<string, unknown>) => {
      if (!keepSearchConditions) return;
      const params = convertToURLSearchParams(searchQueryForUrl);
      saveLastListSearch(`?${params.toString()}`);
    },
    [keepSearchConditions]
  );

  const handleSearch = useCallback(
    (query: ReceivedInvoicesSearchQuery) => {
      const id = param.get('id') || undefined;
      const page = query?.page;

      if (Object.keys(query).length === 1 && 'page' in query) {
        const newSearchQuery = { ...searchQuery, page };
        const urlPayload = { ...newSearchQuery, ...(id ? { id } : {}) };
        updateSearchParams(urlPayload);
        persistLastSearch(newSearchQuery);
        setSearchQuery(newSearchQuery);
        return;
      }

      const searchPayload = convertToInvoiceFilterPayload(query);
      const newSearchQuery = { ...searchPayload, page: 1 };
      updateSearchParams(newSearchQuery);
      persistLastSearch(newSearchQuery);
      setSearchQuery(newSearchQuery);
    },
    [updateSearchParams, searchQuery, param, persistLastSearch]
  );

  return { searchQuery, handleSearch };
};
